/** Límites y catálogos de la pantalla de gestión de la ficha de Google. */

/** Google trunca la respuesta a una reseña en 4096 caracteres; el backend la rechaza antes. */
export const REPLY_MAX_LENGTH = 4096;

/** Longitud máxima del cuerpo de una publicación (novedad u oferta). */
export const POST_MAX_LENGTH = 1500;

/** Rangos de días que ofrece el selector de estadísticas. */
export const METRIC_RANGES = [7, 30, 90] as const;

export const DEFAULT_METRIC_RANGE = 30;

export const METRIC_RANGE_LABELS: Record<number, string> = {
  7: 'Últimos 7 días',
  30: 'Últimos 30 días',
  90: 'Últimos 90 días',
};

/**
 * Cómo se le explica al comercio el modo con el que quedó vinculada su ficha.
 *
 * Es la mitad legible del estado: sin esto la tarjeta solo dice "CONECTADO" en
 * verde y el comercio no sabe si Plick puede escribir en su ficha o solo leerla
 * — la duda textual del beta tester ("en el demo lo vi conectado… no me queda
 * claro cómo será esa integración").
 */
export const LINK_MODE_LABELS: Record<string, string> = {
  PLACE_ID: 'Solo lectura',
  AGENCY_MANAGED: 'Administrada por Plick',
  OAUTH_OWNER: 'Tu cuenta de Google conectada',
};

export type GooglePostTopicType = 'STANDARD' | 'OFFER';

export type GooglePostCtaType = 'ORDER' | 'BOOK' | 'SHOP' | 'LEARN_MORE' | 'SIGN_UP' | 'CALL';

export const POST_TOPIC_LABELS: Record<GooglePostTopicType, string> = {
  STANDARD: 'Novedad',
  OFFER: 'Oferta',
};

export const POST_CTA_LABELS: Record<GooglePostCtaType, string> = {
  ORDER: 'Pedir en línea',
  BOOK: 'Reservar',
  SHOP: 'Comprar',
  LEARN_MORE: 'Más información',
  SIGN_UP: 'Registrarse',
  CALL: 'Llamar',
};

/**
 * Qué le falta al comercio para poder ESCRIBIR en su ficha. Acompaña a un control
 * deshabilitado, no es un tutorial.
 *
 * CADUCA: la frase "ese permiso está en trámite" describe la aprobación pendiente
 * de Google al proyecto de Plick. Cuando Google apruebe, este texto miente y hay
 * que reescribirlo (junto con READ_BLOCKED_MESSAGE y la sección "Lo que se activa
 * en la siguiente etapa").
 */
export const WRITE_BLOCKED_MESSAGE =
  'Tu ficha está vinculada en modo de solo lectura: Plick puede leer lo que Google ya muestra en público, pero todavía no puede cambiar nada en tu ficha. Para eso Plick necesita quedar como administrador, y ese permiso está en trámite.';

/**
 * Por qué un dato de LECTURA no se puede mostrar (estadísticas, texto de reseñas).
 * Va aparte del de escritura porque ahí el problema no es escribir: es que Google
 * ni siquiera nos deja consultar el dato, y decir "no puedes escribir" frente a
 * una pantalla vacía de números no explica nada.
 *
 * CADUCA con la misma aprobación de Google que WRITE_BLOCKED_MESSAGE.
 */
export const READ_BLOCKED_MESSAGE =
  'Google reserva esta información a las fichas que administra Plick. Tu ficha está vinculada en modo de solo lectura, así que todavía no podemos consultarla.';

/**
 * Etiqueta del CTA que acompaña a los mensajes de bloqueo.
 *
 * NO promete que el comercio desbloquee nada: el primer bloqueo es una aprobación
 * de Google al proyecto de Plick, no un trámite del comercio. "Solicitar acceso
 * administrado" sugería lo contrario.
 */
export const MANAGED_LINK_CTA_LABEL = 'Quiero enterarme cuando esté listo';

/** Mismo canal que el CTA de la tarjeta de la integración. */
export const MANAGED_LINK_HELP_URL =
  'https://api.whatsapp.com/send?phone=+529935318237&text=Quiero%20que%20Plick%20administre%20mi%20ficha%20de%20Google.';

/**
 * Modelo de permisos explicado ANTES de vincular.
 *
 * Vivía en `Integrationsv3/constants.tsx` como `disclaimer`/`notes` de la tarjeta
 * y no se pintaba nunca: `Modal.tsx` los renderiza en una ruta a la que `Card.tsx`
 * no navega cuando el comercio tiene el módulo. Era la única explicación del
 * modelo de permisos en toda la integración. Se exporta desde aquí para que la
 * tarjeta y el modal de vinculación compartan un solo texto.
 */
export const CONNECT_DISCLAIMER =
  'Elige la ficha de TU negocio, la que ya existe en Google Maps. No necesitas la contraseña de la cuenta de Google que la creó.';

export const CONNECT_NOTES = [
  'Hoy Plick lee tu ficha: tu calificación, cuántas reseñas tienes y los datos que Google muestra en público. No cambia nada en Google.',
  'Todavía no puede responder reseñas, publicar novedades ni mover el botón "Pedir en línea" a tu menú Plick. Eso llega en una segunda etapa, cuando Plick quede como administrador de tu ficha. Te avisamos y te ayudamos con ese paso.',
];

/**
 * Aviso de propiedad, permanente y arriba de los resultados.
 *
 * `connectByPlaceId` comprueba contra Google que la ficha EXISTA, pero no que sea
 * del comercio que la vincula: el beta tester lo sospechó ("¿alguien más solo por
 * joder podría colocar a Yeivis en su menú?") y tenía razón. El aviso de distancia
 * no cubre el caso —solo salta a más de 1 km—, así que el límite se dice con
 * todas sus letras en vez de esquivarlo.
 */
export const OWNERSHIP_WARNING =
  'Elige la ficha de tu propio negocio. La calificación y las reseñas que veas en Plick —y las que más adelante se puedan mostrar en tu menú— van a ser las de la ficha que elijas aquí. Plick no puede comprobar por sí solo que una ficha te pertenezca, así que la elección correcta depende de ti.';

/** Qué pasa exactamente al vincular. Contesta "no sé qué hace este botón". */
export const CONNECT_CONSEQUENCE =
  'Al vincular, Plick va a leer de Google lo que ya es público en tu ficha: nombre, dirección, teléfono, horarios, tu calificación y cuántas reseñas tienes. Eso se muestra en tu panel. Plick no cambia nada en Google, y puedes desvincular la ficha cuando quieras.';
